import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Link2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { useDeviationDetailQuery, type DeviationSummary } from '@/lib/api/deviations';
import { deviationStatusLabel, deviationTypeLabel } from '@/lib/contract-labels';
import { formatDate } from '@/lib/utils/format';

/**
 * Deviation card — one tracked deviation, with severity + status chips,
 * deviation_type, the linked deliverable by name, the waiver reason, a
 * history disclosure and created-on meta.
 *
 * Visual contract mirrors `screens-modules.js → dvRow()` adapted to a
 * card surface (the screen is a vertical list of cards rather than a
 * dense table; matches the rfis screen pattern).
 *
 * Severity → posture vocabulary (matches issue-severity-chip):
 *   Critical → critical
 *   High     → critical
 *   Medium   → at-risk
 *   Low      → unassessed
 *
 * Status → posture vocabulary:
 *   open          → critical    (still red until claimed)
 *   in_resolution → at-risk     (mitigation in flight)
 *   resolved      → healthy
 *   waived        → unassessed  (terminal, not a remediation)
 *
 * Each card carries a left-rule accent that matches the severity color so
 * critical deviations read at-a-glance.
 */

export interface DeviationCardProps {
  engagementId: string;
  deviation: DeviationSummary;
  /** Opens the status-transition modal for this deviation. */
  onTransition?: () => void;
}

export function DeviationCard({ engagementId, deviation, onTransition }: DeviationCardProps) {
  const [showHistory, setShowHistory] = useState(false);
  const sev = mapSeverityToPosture(deviation.severity);
  const status = mapStatusToPosture(deviation.status);
  const accent =
    sev.posture === 'critical'
      ? 'border-l-posture-critical'
      : sev.posture === 'at-risk'
        ? 'border-l-posture-at-risk'
        : 'border-l-posture-unassessed';

  return (
    <Card className={`border-l-4 ${accent}`}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-baseline gap-3">
          <span className="font-mono text-meta text-ink-500">
            DV-{deviation.deviation_entry_id.slice(0, 8)}
          </span>
          <h3 className="flex-1 text-body font-semibold text-ink-900">{deviation.title}</h3>
          <PostureChip status={sev.posture}>{sev.label}</PostureChip>
          <PostureChip status={status.posture}>{status.label}</PostureChip>
          {onTransition && deviation.status !== 'resolved' && deviation.status !== 'waived' ? (
            <Button type="button" variant="outline" size="sm" onClick={onTransition}>
              Update status
            </Button>
          ) : null}
        </div>

        {deviation.description ? (
          <p className="text-body-sm text-ink-700">{deviation.description}</p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3 text-meta text-ink-500">
          <span>
            Type{' '}
            <span className="text-ink-700">{deviationTypeLabel(deviation.deviation_type)}</span>
          </span>
          <span>
            Logged{' '}
            <span className="font-mono text-ink-700">{formatDate(deviation.created_at)}</span>
          </span>
          {deviation.detected_by === 'system' ? (
            // Raised by a detection rule, not typed in by a person. Says
            // which fact triggered it so the reader can trust (or challenge) it.
            <span className="inline-flex items-center gap-1 rounded-chip bg-ink-100 px-2 py-0.5 text-ink-700">
              Raised automatically
              {detectionReason(deviation.detected_rule)
                ? ` · ${detectionReason(deviation.detected_rule)}`
                : ''}
            </span>
          ) : null}
          {deviation.checkpoint_name ? (
            <span className="inline-flex items-center gap-1 rounded-chip bg-ink-100 px-2 py-0.5 text-ink-700">
              Checkpoint · {deviation.checkpoint_name}
            </span>
          ) : null}
          {deviation.source_document_title ? (
            <span className="inline-flex items-center gap-1 rounded-chip bg-ai-surface px-2 py-0.5 text-ai-ink">
              Read from {deviation.source_document_title}
              {deviation.source_page_number ? `, p.${deviation.source_page_number}` : ''}
            </span>
          ) : null}
          {deviation.escalation_breached_at ? (
            <span className="font-medium text-posture-critical">
              Response deadline missed {formatDate(deviation.escalation_breached_at)}
            </span>
          ) : null}
        </div>

        {deviation.status === 'waived' && deviation.waiver_reason ? (
          <div className="rounded-card border border-ink-100 bg-paper-50 px-3 py-2 text-body-sm text-ink-700">
            <span className="text-meta uppercase tracking-wide text-ink-500">Waived because</span>
            <p className="mt-0.5">{deviation.waiver_reason}</p>
          </div>
        ) : null}

        {deviation.deliverable_entry_id ? (
          // C2-31: the deliverable by its name and SOW reference, linked to
          // its drawer on the Deliverables tab, never by its id.
          <div className="flex items-center gap-2 rounded-card bg-paper-50 px-3 py-2 text-meta text-ink-700">
            <Link2 className="h-3 w-3" aria-hidden="true" />
            <span>Linked deliverable</span>
            <Link
              to="/engagements/$engagementId/contract/deliverables"
              params={{ engagementId }}
              search={{ open: deviation.deliverable_entry_id }}
              className="font-medium text-ink-900 underline-offset-2 hover:underline"
            >
              {linkedDeliverableWords(deviation)}
            </Link>
          </div>
        ) : (
          <div className="text-meta text-ink-500">No linked deliverable.</div>
        )}

        <div>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            aria-expanded={showHistory}
            className="inline-flex items-center gap-1 text-meta font-medium text-ink-700 hover:text-ink-900"
          >
            {showHistory ? (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            )}
            History
          </button>
          {showHistory ? (
            <DeviationHistory deviationEntryId={deviation.deviation_entry_id} />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** "D-14 · Test plan"; the title alone without a reference; a plain phrase as a last resort. */
export function linkedDeliverableWords(
  d: Pick<DeviationSummary, 'deliverable_title' | 'deliverable_sow_ref'>,
): string {
  const title = d.deliverable_title ?? null;
  const ref = d.deliverable_sow_ref ?? null;
  if (title && ref) return `${ref} · ${title}`;
  return title ?? ref ?? 'Open the deliverable';
}

/**
 * Status changes with their notes, oldest first (C2-19). A waiver's reason
 * is the note on its transition, so "Waived" reads with its why. Loaded on
 * demand: the list endpoint carries the current state, not the timeline.
 */
function DeviationHistory({ deviationEntryId }: { deviationEntryId: string }) {
  const detail = useDeviationDetailQuery(deviationEntryId);
  if (detail.isLoading) {
    return (
      <div className="mt-2 flex items-center gap-2 text-meta text-ink-500">
        <Spinner /> Loading history…
      </div>
    );
  }
  if (detail.error) return <ErrorMessage error={detail.error} />;
  const transitions = detail.data?.transitions ?? [];
  if (transitions.length === 0) {
    return <p className="mt-2 text-meta text-ink-500">No status changes yet.</p>;
  }
  return (
    <ol className="mt-2 space-y-1.5 border-l border-ink-100 pl-3">
      {transitions.map((t) => (
        <li key={t.id} className="text-body-sm text-ink-700">
          <span className="font-medium text-ink-900">
            {t.from_status ? `${deviationStatusLabel(t.from_status)} → ` : ''}
            {deviationStatusLabel(t.to_status)}
          </span>
          <span className="ml-2 font-mono text-meta text-ink-500">{formatDate(t.created_at)}</span>
          {t.note ? <p className="text-meta text-ink-500">“{t.note}”</p> : null}
        </li>
      ))}
    </ol>
  );
}

interface PostureMap {
  posture: PostureStatus;
  label: string;
}

function mapSeverityToPosture(severity: string): PostureMap {
  switch (severity) {
    case 'Critical':
      return { posture: 'critical', label: 'Critical' };
    case 'High':
      return { posture: 'critical', label: 'High' };
    case 'Medium':
      return { posture: 'at-risk', label: 'Medium' };
    case 'Low':
      return { posture: 'unassessed', label: 'Low' };
    default:
      return { posture: 'unassessed', label: severity || '—' };
  }
}

function mapStatusToPosture(status: string): PostureMap {
  const label = status ? deviationStatusLabel(status) : '—';
  switch (status) {
    case 'open':
      return { posture: 'critical', label };
    case 'in_resolution':
      return { posture: 'at-risk', label };
    case 'resolved':
      return { posture: 'healthy', label };
    default:
      return { posture: 'unassessed', label };
  }
}

/** Plain-language reason for a system-raised deviation's detection rule. */
function detectionReason(rule: string | null | undefined): string | null {
  switch (rule) {
    case 'deliverable_rejected':
      return 'deliverable was rejected';
    case 'deliverable_revise':
      return 'deliverable was returned for revision';
    case 'deliverable_overdue':
      return 'deliverable passed its due date';
    case 'checkpoint_criterion_failed':
      return 'checkpoint criterion scored red';
    default:
      return null;
  }
}
